import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../style/App.scss';
import {
  Box,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import Api from '../../api';
import Loader from '../../components/shared/Loader';
import EmptyState from '../../components/list/EmptyState';
import {
  mainColors,
  accentColors,
  successColors,
  alertColors,
  secondaryColors
} from '../../theme/themeConfig';
import { formatDate } from '../../utils/general';

// ─── Lead status values from API ─────────────────────────────────────────────
const LeadStatus = ['All', 'Converted', 'Draft', 'Rejected', 'Working Lead'];

// ─── Status config ────────────────────────────────────────────────────────────
interface StatusConfig {
  bg: string;
  color: string;
  dot: string;
  label: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  Converted: {
    bg: successColors.SUCCESS_6,
    color: successColors.SUCCESS,
    dot: successColors.SUCCESS_7,
    label: 'Converted'
  },
  Draft: {
    bg: secondaryColors.SECONDARY_6,
    color: secondaryColors.SECONDARY,
    dot: secondaryColors.SECONDARY_1,
    label: 'Draft'
  },
  Rejected: {
    bg: '#FFF0F0',
    color: alertColors.ALERT,
    dot: alertColors.ALERT,
    label: 'Rejected'
  },
  'Working Lead': {
    bg: mainColors.MAIN_LIGHT,
    color: mainColors.MAIN,
    dot: mainColors.MAIN,
    label: 'Working Lead'
  },
  proposalLost: {
    bg: alertColors.ALERT_6,
    color: alertColors.ALERT_7,
    dot: alertColors.ALERT_7,
    label: 'Proposal Lost'
  },
  submitedProposal: {
    bg: accentColors.ACCENT_6,
    color: accentColors.ACCENT,
    dot: accentColors.ACCENT,
    label: 'Proposal Submitted'
  }
};

const ACCENT_COLORS: Record<string, string> = {
  Converted: successColors.SUCCESS_7,
  Draft: secondaryColors.SECONDARY_1,
  Rejected: alertColors.ALERT,
  'Working Lead': mainColors.MAIN,
  proposalLost: alertColors.ALERT_7,
  submitedProposal: accentColors.ACCENT
};

// ─── Styled components ────────────────────────────────────────────────────────
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  width: '100%'
}));

const ToolbarBox = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.25),
  justifyContent: 'space-between'
}));

const TableCard = styled(Box)(({ theme }) => ({
  background: theme.palette.background.box,
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: theme.spacing(1.5),
  boxShadow: '0px 1px 3px rgba(15,28,46,0.06), 0px 1px 2px rgba(15,28,46,0.04)',
  overflow: 'hidden'
}));

const StyledTableHead = styled(TableHead)(() => ({
  background: '#F8FAFD'
}));

const StyledHeaderCell = styled(TableCell)(() => ({
  borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
  color: secondaryColors.SECONDARY,
  fontSize: '10.5px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  padding: '10px 14px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
}));

const StyledRow = styled(TableRow)(() => ({
  '&:hover': { background: '#F5F9FD' },
  '&:last-child td': { borderBottom: 'none' },
  cursor: 'pointer',
  transition: 'background 0.12s'
}));

const StyledCell = styled(TableCell)(() => ({
  borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
  fontSize: '12.5px',
  padding: '11px 14px',
  verticalAlign: 'middle'
}));

const LeadCard = styled(Box)(({ theme }) => ({
  '&:hover': {
    boxShadow:
      '0px 4px 14px rgba(15,28,46,0.09), 0px 2px 4px rgba(15,28,46,0.05)',
    transform: 'translateY(-1px)'
  },
  background: theme.palette.background.box,
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: theme.spacing(1.5),
  boxShadow: '0px 1px 3px rgba(15,28,46,0.06), 0px 1px 2px rgba(15,28,46,0.04)',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  transition: 'box-shadow 0.18s ease, transform 0.18s ease'
}));

const ViewToggleBtn = styled(Button)<{ active?: string }>(({ active }) => ({
  '&:hover': {
    background:
      active === 'true' ? mainColors.MAIN : secondaryColors.SECONDARY_6
  },
  background: active === 'true' ? mainColors.MAIN : 'transparent',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '8px',
  color: active === 'true' ? '#fff' : secondaryColors.SECONDARY,
  minWidth: 36,
  padding: '5px 10px'
}));

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) {
    return (
      <Chip
        label={status}
        size="small"
        sx={{ fontSize: '10.5px', fontWeight: 700 }}
      />
    );
  }
  return (
    <Box
      sx={{
        alignItems: 'center',
        background: cfg.bg,
        borderRadius: '20px',
        display: 'inline-flex',
        gap: '5px',
        padding: '3px 9px'
      }}
    >
      <Box
        sx={{
          background: cfg.dot,
          borderRadius: '50%',
          flexShrink: 0,
          height: 6,
          width: 6
        }}
      />
      <Typography
        sx={{
          color: cfg.color,
          fontSize: '10.5px',
          fontWeight: 700,
          whiteSpace: 'nowrap'
        }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
}

function ProposalBadge({ status }: { status: string | null }) {
  if (!status || status === 'null' || status === 'undefined') {
    return (
      <Typography
        sx={{
          color: secondaryColors.SECONDARY_1,
          fontSize: '11px',
          fontStyle: 'italic'
        }}
      >
        —
      </Typography>
    );
  }
  const colorMap: Record<string, { bg: string; color: string }> = {
    Accepted: { bg: successColors.SUCCESS_6, color: successColors.SUCCESS },
    Recalled: { bg: alertColors.ALERT_6, color: alertColors.ALERT_7 },
    Seen: { bg: accentColors.ACCENT_6, color: accentColors.ACCENT },
    Sent: { bg: mainColors.MAIN_LIGHT, color: mainColors.MAIN_2 }
  };
  const style = colorMap[status] || {
    bg: secondaryColors.SECONDARY_6,
    color: secondaryColors.SECONDARY
  };
  return (
    <Box
      sx={{
        background: style.bg,
        borderRadius: '5px',
        display: 'inline-flex',
        padding: '2px 7px'
      }}
    >
      <Typography
        sx={{ color: style.color, fontSize: '10.5px', fontWeight: 600 }}
      >
        {status}
      </Typography>
    </Box>
  );
}

function TypeBadge({ projectType }: { projectType: string }) {
  const isRecurring = projectType === 'Regular' || projectType === 'regular';
  return (
    <Box
      sx={{
        background: isRecurring ? accentColors.ACCENT_6 : '#EEF4FF',
        borderRadius: '5px',
        display: 'inline-flex',
        padding: '3px 8px'
      }}
    >
      <Typography
        sx={{
          color: isRecurring ? accentColors.ACCENT : '#4A6CF7',
          fontSize: '10.5px',
          fontWeight: 600,
          whiteSpace: 'nowrap'
        }}
      >
        {isRecurring ? '↻ Recurring' : '◆ One-time'}
      </Typography>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface SortIconProps {
  activeDir: 'asc' | 'desc';
  activeField: string;
  field: string;
}

function SortIcon({ field, activeField, activeDir }: SortIconProps) {
  if (activeField !== field)
    return <ArrowDownwardIcon sx={{ fontSize: 11, ml: 0.5, opacity: 0.25 }} />;
  return activeDir === 'asc' ? (
    <ArrowUpwardIcon sx={{ color: mainColors.MAIN, fontSize: 11, ml: 0.5 }} />
  ) : (
    <ArrowDownwardIcon sx={{ color: mainColors.MAIN, fontSize: 11, ml: 0.5 }} />
  );
}

const ListingLeads: React.FC = function ListingLeads() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── existing state (preserved) ──────────────────────────────────────────────
  const [data, setData] = useState<any>(null);
  const [fireLeadApi, setFireLeadApi] = useState<boolean>(false);
  const [emptyState, setEmptyState] = useState<boolean>(false);
  const pageNumber = useRef<number>(1);
  const [selectInput, setSelectInput] = useState<string>('');
  const [filterdData, setFilterdData] = useState<any[]>([]);

  // ── new state ────────────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<'list' | 'tile'>('list');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // ── API actions (preserved) ──────────────────────────────────────────────────
  const getAction = Api.leadsApi.leadApi.getLeadsPaginated;
  const params = {
    items: 20,
    page: pageNumber.current,
    url: 'api/leads'
  };

  const { data: mainResponse, mutate } = useSWR(
    fireLeadApi ? params : null,
    getAction
  );

  const handleOpen = () => navigate('/app/leads/new');

  // ── effects (preserved) ──────────────────────────────────────────────────────
  useEffect(() => {
    if (selectInput && selectInput !== '' && selectInput !== 'All') {
      const newData = data.filter((item: any) => item.status === selectInput);
      setFilterdData(newData);
    } else if (data && data.length > 0) {
      setFilterdData(data);
    } else {
      setFilterdData([]);
    }
  }, [selectInput]);

  useEffect(() => {
    if (mainResponse && mainResponse.data) {
      setEmptyState(mainResponse?.data?.length === 0 && !data);
      setFireLeadApi(false);
      const newData = data?.length > 0 ? [...data] : [];
      mainResponse?.data?.forEach((item: any) => {
        const client =
          item?.clientCompanyName && item?.clientCompanyName !== ''
            ? item?.clientCompanyName
            : `${item?.clientFirstName} ${item?.clientLastName}`;
        newData.push({
          ...item,
          client,
          icon: `${item?.proposalstatus}`,
          tooltip: `${t('TOOLTIP.PROPOSAL.proposal')} ${item?.proposalstatus}`
        });
      });
      setData(newData);
      if (selectInput && selectInput !== '' && selectInput !== 'All') {
        const newDataFiltered = newData.filter(
          (item: any) => item.status === selectInput
        );
        setFilterdData(newDataFiltered);
      } else {
        setFilterdData(newData);
      }
    }
  }, [mainResponse]);

  useEffect(() => {
    if (data && data.length === 0 && !emptyState) {
      mutate();
    }
  }, [data]);

  useEffect(() => {
    setFireLeadApi(true);
    return () => {
      setData(null);
      mutate(null, true);
    };
  }, []);

  if (!data) {
    return <Loader customColor="primary" customSize={48} />;
  }

  // ── Shared row click ─────────────────────────────────────────────────────────
  const handleRowClick = (item: any) => {
    navigate('/app/leads/edit-lead', { state: { id: item.id } });
  };

  // ─── Sorting ────────────────────────────────────────────────────────────────
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedData = [...filterdData].sort((a: any, b: any) => {
    const aVal = a[sortField] ?? '';
    const bVal = b[sortField] ?? '';
    const cmp = String(aVal).localeCompare(String(bVal), undefined, {
      numeric: true,
      sensitivity: 'base'
    });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // ─── List view ──────────────────────────────────────────────────────────────
  const renderList = () => (
    <TableCard>
      <TableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              {[
                { field: 'name', label: 'Lead Name' },
                { field: 'projectType', label: 'Type' },
                { field: 'client', label: 'Client' },
                { field: 'createdAt', label: 'Created' },
                { field: 'status', label: 'Status' },
                { field: 'proposalstatus', label: 'Proposal' }
              ].map(({ label, field }) => (
                <StyledHeaderCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{
                    '&:hover': { color: mainColors.MAIN },
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <Box sx={{ alignItems: 'center', display: 'inline-flex' }}>
                    {label}
                    <SortIcon
                      activeDir={sortDir}
                      activeField={sortField}
                      field={field}
                    />
                  </Box>
                </StyledHeaderCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {sortedData.map((item: any) => (
              <StyledRow key={item.id} onClick={() => handleRowClick(item)}>
                <StyledCell sx={{ pl: 2 }}>
                  <Typography
                    sx={{
                      color: 'primary.400',
                      fontSize: '12.5px',
                      fontWeight: 700
                    }}
                  >
                    {item.name}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <TypeBadge projectType={item.projectType} />
                </StyledCell>
                <StyledCell>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: '12.5px' }}
                  >
                    {item.client}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontSize: '12.5px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.createdAt ? formatDate(item.createdAt) : '—'}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <StatusChip status={item.status} />
                </StyledCell>
                <StyledCell>
                  <ProposalBadge status={item?.proposalstatus} />
                </StyledCell>
              </StyledRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {filterdData.length === 0 && <EmptyState />}
    </TableCard>
  );

  // ─── Tile view ──────────────────────────────────────────────────────────────
  const renderTiles = () => (
    <Box>
      {filterdData.length === 0 ? (
        <EmptyState />
      ) : (
        <Grid container spacing={1.75}>
          {filterdData.map((item: any) => {
            const accentColor = ACCENT_COLORS[item.status] || mainColors.MAIN;
            return (
              <Grid item key={item.id} lg={3} md={4} sm={6} xs={12}>
                <LeadCard onClick={() => handleRowClick(item)}>
                  {/* Accent bar */}
                  <Box
                    sx={{ background: accentColor, height: 3, width: '100%' }}
                  />

                  <Box sx={{ flex: 1, padding: '14px 16px' }}>
                    {/* Header row */}
                    <Box
                      sx={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1.25
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                        <Typography
                          sx={{
                            color: 'primary.400',
                            fontSize: '13px',
                            fontWeight: 700,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.name}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'text.secondary',
                            fontSize: '11.5px',
                            mt: 0.25
                          }}
                        >
                          {item.client}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Status */}
                    <StatusChip status={item.status} />

                    {/* Fields grid */}
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: '1fr 1fr',
                        mt: 1.25
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            color: secondaryColors.SECONDARY_1,
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '.5px',
                            mb: 0.25,
                            textTransform: 'uppercase'
                          }}
                        >
                          Type
                        </Typography>
                        <TypeBadge projectType={item.projectType} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            color: secondaryColors.SECONDARY_1,
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '.5px',
                            mb: 0.25,
                            textTransform: 'uppercase'
                          }}
                        >
                          Created
                        </Typography>
                        <Typography
                          sx={{
                            color: 'text.primary',
                            fontSize: '12px',
                            fontWeight: 600
                          }}
                        >
                          {item.createdAt ? formatDate(item.createdAt) : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            color: secondaryColors.SECONDARY_1,
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '.5px',
                            mb: 0.25,
                            textTransform: 'uppercase'
                          }}
                        >
                          Proposal
                        </Typography>
                        <ProposalBadge status={item?.proposalstatus} />
                      </Box>
                    </Box>
                  </Box>
                </LeadCard>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );

  return (
    <PageContainer>
      {/* Page header */}
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Typography
          sx={{
            color: 'primary.400',
            fontSize: '24px',
            fontWeight: 800,
            letterSpacing: '-0.4px'
          }}
        >
          {t('MENU.leads')}
        </Typography>
        <Button onClick={handleOpen} variant="contained">
          <Typography variant="primary-bold">{t('GENERAL.create')}</Typography>
        </Button>
      </Box>

      {/* Toolbar */}
      <ToolbarBox>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75
          }}
        >
          {LeadStatus.map((status) => {
            const isActive =
              status === 'All'
                ? !selectInput || selectInput === '' || selectInput === 'All'
                : selectInput === status;
            return (
              <Button
                key={status}
                onClick={() => setSelectInput(status === 'All' ? '' : status)}
                size="small"
                sx={{
                  '&:hover': {
                    background: isActive
                      ? mainColors.MAIN_2
                      : secondaryColors.SECONDARY_6,
                    border: `1px solid ${
                      isActive ? mainColors.MAIN_2 : secondaryColors.SECONDARY_6
                    }`
                  },
                  background: isActive ? mainColors.MAIN : 'white',
                  border: `1px solid ${
                    isActive ? mainColors.MAIN : secondaryColors.SECONDARY_6
                  }`,
                  borderRadius: '20px',
                  color: isActive ? 'white' : secondaryColors.SECONDARY,
                  fontSize: '11.5px',
                  fontWeight: 600,
                  px: 1.75,
                  py: '4px',
                  textTransform: 'none',
                  transition: 'all 0.15s'
                }}
              >
                {status}
              </Button>
            );
          })}
          <Typography
            sx={{
              background: secondaryColors.SECONDARY_6,
              borderRadius: '20px',
              color: secondaryColors.SECONDARY,
              fontSize: '11px',
              fontWeight: 700,
              ml: 0.5,
              padding: '2px 8px'
            }}
          >
            {filterdData.length} leads
          </Typography>
        </Box>

        {/* View toggle */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <ViewToggleBtn
            active={viewMode === 'list' ? 'true' : 'false'}
            onClick={() => setViewMode('list')}
            size="small"
          >
            <ListIcon sx={{ fontSize: 18 }} />
          </ViewToggleBtn>
          <ViewToggleBtn
            active={viewMode === 'tile' ? 'true' : 'false'}
            onClick={() => setViewMode('tile')}
            size="small"
          >
            <GridViewIcon sx={{ fontSize: 18 }} />
          </ViewToggleBtn>
        </Box>
      </ToolbarBox>

      {/* Views */}
      {viewMode === 'list' ? renderList() : renderTiles()}
    </PageContainer>
  );
};

export default ListingLeads;
